import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { LogIn, UserPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    gamerTag: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Load reCAPTCHA when switching to signup mode
  React.useEffect(() => {
    if (isSignup && window.grecaptcha) {
      // Give the DOM time to render the div before calling render
      setTimeout(() => {
        try {
          const recaptchaElement = document.getElementById('recaptcha-container');
          if (recaptchaElement && !recaptchaElement.hasChildNodes()) {
            window.grecaptcha.render('recaptcha-container', {
              'sitekey': '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
              'theme': 'dark'
            });
          }
        } catch (e) {
          console.log('reCAPTCHA already rendered');
        }
      }, 100);
    }
  }, [isSignup]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      let payload;
      
      if (isSignup) {
        // Get reCAPTCHA token for signup
        const captchaToken = window.grecaptcha.getResponse();
        
        if (!captchaToken) {
          setError('Please complete the CAPTCHA verification.');
          setIsLoading(false);
          return;
        }
        
        payload = { 
          email: formData.email, 
          password: formData.password, 
          gamer_tag: formData.gamerTag,
          captcha_token: captchaToken
        };
      } else {
        payload = { email: formData.email, password: formData.password };
      }

      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Redirect admin to admin dashboard, customers to portal
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      setIsLoading(false);
      // Reset reCAPTCHA on error if signup
      if (isSignup && window.grecaptcha) {
        window.grecaptcha.reset();
      }
    }
  };

  return (
    <div className="app-content bg-[#050505] min-h-screen">
      <Navbar />
      
      <section className="py-32 min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full mx-6">
          <div className="bg-[#0A0A0A] border border-red-500/30 p-8" data-testid="login-card">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-black uppercase mb-2 text-red-500" data-testid="login-title">
                {isSignup ? 'CREATE ACCOUNT' : 'CUSTOMER LOGIN'}
              </h1>
              <p className="text-gray-400 font-['Rajdhani']">
                {isSignup ? 'Join AFK Boosts' : 'Access your order history'}
              </p>
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-500/50 p-4 mb-6" data-testid="error-message">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                  EMAIL *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="email-input"
                  className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                  PASSWORD *
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="password-input"
                  className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all"
                  placeholder="Enter password"
                />
              </div>

              {isSignup && (
                <div>
                  <label className="text-sm uppercase tracking-wider text-gray-400 mb-2 block">
                    GAMER TAG (Optional)
                  </label>
                  <input
                    type="text"
                    name="gamerTag"
                    value={formData.gamerTag}
                    onChange={handleChange}
                    data-testid="gamer-tag-input"
                    className="w-full bg-black border border-white/10 focus:border-red-500 text-white h-12 px-4 font-['Rajdhani'] focus:ring-1 focus:ring-red-500 transition-all"
                    placeholder="Your gamer tag"
                  />
                </div>
              )}

              {isSignup && (
                <div className="flex justify-center">
                  <div id="recaptcha-container"></div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                data-testid="submit-button"
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white font-bold py-4 px-8 uppercase tracking-widest transition-all hover:shadow-[0_0_20px_rgba(255,0,0,0.5)] flex items-center justify-center gap-3"
              >
                {isLoading ? (
                  'PROCESSING...'
                ) : (
                  <>
                    {isSignup ? <UserPlus size={20} /> : <LogIn size={20} />}
                    {isSignup ? 'SIGN UP' : 'LOGIN'}
                  </>
                )}
              </button>
            </form>

            {!isSignup && (
              <div className="mt-4 text-center">
                <Link 
                  to="/forgot-password"
                  className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError('');
                }}
                data-testid="toggle-mode"
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                {isSignup ? 'Already have an account? Login' : "Don't have an account? Sign up"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoginPage;